import { FC, Fragment, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowUp } from '@mui/icons-material';

import { IColumnDefinition } from '@common/types';
import {
  CareNotesColumnNames,
  ICareNotesTableEntries,
  ICareNotesTableFilters,
  useCareNotesTable,
} from '@views/pages/Patient/components/CareNotes/hooks';
import { AddCareNote } from '@views/pages/Patient/components/CareNotes/components/AddCareNote.tsx';
import { theme } from '@store';
import {
  TableNoResultPlaceholder,
  TruncatedTextWithConditionalTooltip,
} from '@components';

interface ICareNotesTableProps {
  rows: ICareNotesTableEntries[];
  columns: IColumnDefinition[];
  filters: ICareNotesTableFilters;
  setFilters: (filters: ICareNotesTableFilters) => void;
  totalCount: number;
  loading: boolean;
  patient_id: string;
  disabled: boolean;
  tabPanelInstrumentHeight: number;
}

export const CareNotesTable: FC<ICareNotesTableProps> = ({
  rows,
  filters,
  columns,
  setFilters,
  totalCount,
  loading,
  patient_id,
  disabled = false,
  tabPanelInstrumentHeight,
}) => {
  const {
    sort_by,
    sort_order,
    onPageIndexChange,
    onPageSizeChange,
    handleChangeOrder,
    notesMaxWidth,
  } = useCareNotesTable({ filters, setFilters });
  return (
    <>
      {rows.length > 0 && !loading ? (
        <>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 0,
              boxShadow: 'none',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              maxHeight: `calc(100vh - ${tabPanelInstrumentHeight + 100}px)`,
            }}
          >
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow
                  sx={{
                    borderRadius: 0,
                    '& .MuiTableCell-head': {
                      paddingY: '1rem',
                      bgcolor: theme.palette.headerBackground.main,
                    },
                  }}
                >
                  {columns.map((col, index) => (
                    <TableCell
                      key={`${col.id}-${index}`}
                      align='left'
                      sx={{ width: col.width, minWidth: 0 }}
                    >
                      <Box sx={{ minWidth: col.width }}>
                        {col.sortable ? (
                          <TableSortLabel
                            active={true}
                            direction={col.id === sort_by ? sort_order : 'asc'}
                            onClick={() =>
                              handleChangeOrder(col.id as CareNotesColumnNames)
                            }
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  return (
                    <Fragment key={row.id}>
                      <CollapsibleRow
                        row={row}
                        patient_id={patient_id}
                        disabled={disabled}
                        notesMaxWidth={notesMaxWidth}
                      />
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            sx={{
              bgcolor: theme.palette.common.white,
              paddingY: '0.75rem',
              zIndex: 100,
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderTop: 'none',
            }}
            component='div'
            count={totalCount}
            page={filters.page - 1}
            rowsPerPage={filters.limit}
            onPageChange={onPageIndexChange}
            onRowsPerPageChange={onPageSizeChange}
          />
        </>
      ) : (
        <Box
          sx={{
            bgcolor: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            padding: '64px 0',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderTop: 'none',
            height: `calc(100vh - ${tabPanelInstrumentHeight + 50}px)`,
          }}
        >
          {loading ? (
            <CircularProgress size='64px' />
          ) : (
            <TableNoResultPlaceholder />
          )}
        </Box>
      )}
    </>
  );
};

interface ICollapsibleRowProps {
  row: ICareNotesTableEntries;
  patient_id: string;
  disabled: boolean;
  notesMaxWidth: string;
}

const CollapsibleRow = ({
  row,
  patient_id,
  disabled,
  notesMaxWidth,
}: ICollapsibleRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <TableRow
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          cursor: 'pointer',

          '& .MuiTableCell-body': {
            paddingY: '1rem',
          },
          '&:hover, &:nth-of-type(even):hover': {
            backgroundColor: theme.palette.hoverTableRowBg.main,
          },
        }}
      >
        <TableCell align='left'>{row.program}</TableCell>
        <TableCell align='left'>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            {row.note_type}
          </Box>
        </TableCell>
        <TableCell align='left'>{row.recorded_minutes}</TableCell>
        <TableCell align='left'>
          <Box sx={{ maxWidth: '15vw' }}>
            <TruncatedTextWithConditionalTooltip
              text={row.provider || '-'}
              sx={{ mt: '.5rem' }}
              variant='body2'
              width='100%'
            />
          </Box>
        </TableCell>
        <TableCell align='left'>{row.created_by_name}</TableCell>
        <TableCell align='left'>{row.last_updated_at}</TableCell>

        <TableCell align='left'>
          {row.notable_call && (
            <Chip
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme => theme.palette.common.white,
                height: '24px',
                width: 'fit-content',
              }}
              label={'Notable'}
            />
          )}
        </TableCell>

        <TableCell sortDirection='desc' component='th' scope='row'>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: '0.5rem',
              justifyContent: 'flex-end',
            }}
          >
            <AddCareNote
              patientId={patient_id}
              variant='edit'
              note_id={row.id}
              disabled={disabled}
            />
            <IconButton size='small'>
              {isExpanded ? (
                <KeyboardArrowUp sx={{ fontSize: '2rem' }} />
              ) : (
                <KeyboardArrowRight sx={{ fontSize: '2rem' }} />
              )}
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow sx={{ bgcolor: theme => theme.palette.grey[100] }}>
        <TableCell
          colSpan={8}
          sx={{
            p: '0',
            border: 0,
          }}
        >
          <Collapse in={isExpanded} timeout='auto' unmountOnExit>
            <Typography
              variant='body1'
              sx={{
                whiteSpace: 'pre-line',
                wordBreak: 'break-all',
                maxWidth: notesMaxWidth,
                m: 2,
              }}
            >
              {row.note}
            </Typography>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
